import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header/header";
import Footer from "../Footer/footer";
import NoorDiaryHighlights from "./noorDiaryHighlights";
import { getNoorDiary, getDiaryById } from "./_request";
import DiariesCarousel from "./DiariesCarousel";

// Assets
import reading from "../../Assets/images/reading.svg";
import writing from "../../Assets/images/writing.svg";
import nimaz from "../../Assets/images/nimaz.svg";
import book from "../../Assets/images/book.svg";
import mahasba from "../../Assets/images/mahasba.svg";

export default function NoorDiary() {
  const [diaries, setDiaries] = useState([]);
  const [selectedDiary, setSelectedDiary] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🧠 Fetch all diaries and set the latest as default
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getNoorDiary();
        if (response?.Result) {
          const sorted = [...response.Diaries].sort(
            (a, b) => parseInt(b.diary.year) - parseInt(a.diary.year),
          );
          setDiaries(sorted);

          const latest = sorted[0]?.diary;
          console.log("Latest Diary:", latest.muqadma);
          setSelectedDiary(latest); // set initially

          if (latest?.id) {
            const detailed = await getDiaryById(latest.id);
            if (detailed?.Result) {
              setArticles(detailed.Articles || []);
              setSelectedDiary(detailed.Diary); // for consistency
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch diary data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 📥 Load articles + diary details for given ID
  const handleDiarySelect = async (diary) => {
    if (diary?.id === selectedDiary?.id) return;

    setLoading(true);
    try {
      const detailed = await getDiaryById(diary.id);
      if (detailed?.Result) {
        setSelectedDiary(detailed.Diary);
        setArticles(detailed.Articles || []);
      }
    } catch (err) {
      console.error("Failed to fetch selected diary data", err);
    } finally {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setLoading(false);
    }
  };

  const handleArticleClick = (articleId) =>
    navigate(`/tableeghat/${articleId}`);

  const groupArticles = (arr, size) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size),
    );

  // Filter out the active article if an id is provided (e.g., via prop or state)
  const filteredArticles = selectedDiary?.activeArticleId
    ? articles.filter(
        (article) =>
          String(article.id) !== String(selectedDiary.activeArticleId),
      )
    : articles;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-primary border-solid" />
      </div>
    );
  }

  // console.log("Selected Diary:", selectedDiary, "PDF:", selectedDiary?.pdf);

  return (
    <>
      <Header />
      <div className="bg-white min-h-screen w-full mt-5 px-5 sm:px-10 md:px-10 lg:px-20 text-black text-center font-nastaleeqRegular font-normal">
        <div className="pt-10 sm:pt-16 md:pt-20">
          {/* 🖼️ Highlights Section */}
          <div className="bg-primaryBG rounded-3xl py-8 sm:py-10 md:py-12 px-5 sm:px-10 md:px-10 lg:px-20">
            {selectedDiary && <NoorDiaryHighlights diary={selectedDiary} />}
          </div>

          {/* 📚 Topics Section */}
          {filteredArticles.length > 0 && (
            <div className="flex flex-col items-center mt-12 transition-all duration-300">
              <p className="font-nastaleeqRegular text-2xl sm:text-3xl md:text-4xl lg:text-4xl mb-6">
                موضوعات
              </p>
              <div className="flex flex-col w-full max-w-screen-xl px-4 sm:flex-row-reverse sm:justify-around sm:gap-6">
                {groupArticles(filteredArticles, 10).map((group, i) => (
                  <ul
                    key={i}
                    className="list-none text-base sm:text-lg md:text-xl lg:text-2xl leading-8 sm:leading-10 p-2 sm:p-4 text-right w-full sm:w-[420px] md:w-[480px] lg:w-[520px] space-y-2"
                    dir="rtl"
                  >
                    {group.map((article) => (
                      <li
                        key={article.id}
                        onClick={() => handleArticleClick(article.id)}
                        className="w-full block px-3 py-2.5 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-200 hover:text-primary transition-colors duration-200 cursor-pointer"
                      >
                        {article.title}
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          )}

          {/* 🌟 Features Section */}
          <div className="bg-primaryBG flex flex-col mb-10 items-center gap-y-8 py-10 px-8 sm:px-12 md:px-16 lg:px-[121px] rounded-3xl mt-12">
            <h1 className="font-nastaleeqKasheeda text-2xl sm:text-3xl md:text-4xl lg:text-4xl">
              ڈائری کی مختلف خصوصیات
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 w-full">
              {[reading, nimaz, writing, book, mahasba].map((img, idx) => {
                const labels = [
                  "یاد امام",
                  "نماز کے اوقات",
                  "لکھنے کی جگہ",
                  "روزانہ کی حدیث",
                  "محاسبہ نفس",
                ];
                return (
                  <div
                    key={idx}
                    className="flex flex-col gap-4 h-36 items-center"
                  >
                    <img
                      src={img}
                      alt={labels[idx]}
                      className="h-20"
                      loading="lazy"
                    />
                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-2xl font-nastaleeqKasheeda">
                      {labels[idx]}
                    </h2>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 📖 Muqadma Section */}
          {(() => {
            // Check if muqadma exists and has actual text content (not just HTML tags)
            if (!selectedDiary?.muqadma || typeof selectedDiary.muqadma !== 'string') return null;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = selectedDiary.muqadma;
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            if (!textContent.trim()) return null;
            
            return (
              <div className="flex flex-col items-center mt-12 mb-20">
                <p className="font-nastaleeqRegular text-2xl sm:text-3xl md:text-4xl lg:text-4xl mb-3">
                  مقدمہ
                </p>
                <div
                  className="w-full text-center text-lg sm:text-xl md:text-xl lg:text-2xl p-6 rounded-3xl group"
                  dangerouslySetInnerHTML={{ __html: selectedDiary.muqadma }}
                />
              </div>
            );
          })()}

          {/* Diary PDF
                    {pdfLoading || !selectedDiary ? (
                        <div className="flex justify-center items-center h-24 mt-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-primary border-solid"></div>
                        </div>
                    ) : selectedDiary.pdf ? (
                        <div className="w-full h-[850px] flex justify-center mt-8 transition-all duration-300 hover:shadow-lg rounded-3xl overflow-hidden">
                            <iframe
                                src={`${DIARY_PDF_URL}/${selectedDiary.pdf}`}
                                className="w-full h-[850px] transition-all duration-300"
                                title="Noor Diary PDF"
                                allowFullScreen
                                onLoad={() => setPdfLoading(false)}
                            ></iframe>
                        </div>
                    ) : (
                        <div className="text-center text-gray-500 py-8 mt-12">پی ڈی ایف دستیاب نہیں</div>
                    )} */}

          {/* 📞 Contact */}
          <div className="flex flex-col justify-center items-center mb-10">
            <p className="tracking-[0.01rem] text-lg sm:text-xl md:text-xl lg:text-2xl mt-8 text-right font-nastaleeqKasheeda">
              <span>نورڈائری </span>حاصل کرنے کے لئے رابطہ فرمائیں
            </p>
            <p className="tracking-[0.01rem] text-lg sm:text-xl md:text-xl lg:text-2xl text-right font-nastaleeqKasheeda max-w-[470px]">
              +92 333 8485338
            </p>
          </div>

          {/* 📅 Previous Diaries */}
          <div className="p-4">
            <DiariesCarousel
              diaries={diaries}
              selectedDiary={selectedDiary}
              handleDiarySelect={handleDiarySelect}
            />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
